import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Consult, ConsultService } from "src/openapi"
import { Box, Modal, Typography } from "src/UILibrary"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import { useLoginContext } from "src/hooks/useLoginContext"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import { ADVISE_TYPE, CONSULT_STATUS, RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import CheckBoxGroupField from "src/components/atom/CheckBoxGroupField"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"
import SelectField from "src/components/atom/SelectField"
import { SelectDataType } from "src/components/atom/SelectDataType"
import { ShowProgress } from "src/states/showProgress"
import InputTextField from "src/components/atom/InputTextField"

interface Props {
  item: Consult
  handleItem: any
  open: boolean
  handleOpen: any
}

export const ConsultAcceptModal = ({ item, handleItem, open, handleOpen }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.inbox" })

  const { areas, accessToken, checkLoginStatus } = useLoginContext()

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const [datetime, setDatetime] = useState<string>(item.hopeDate1 as string)
  const [areaID, setAreaID] = useState<number>(1)
  const [zoomlink, setZoomLink] = useState<string>("")

  const [areaValidationMessage, setAreaValidationMessage] = useState<string>("")
  const [zoomValidationMessage, setZoomValidationMessage] = useState<string>("")

  const getMethodString = () => {
    switch (item.method) {
      case ADVISE_TYPE.MEETING:
        return t("consult_method_meeting")
      case ADVISE_TYPE.CALL:
        return t("consult_method_call")
      case ADVISE_TYPE.ONLINE:
        return t("consult_method_online")
    }
    return ""
  }

  const getReservationDate = () => {
    let ret: SelectDataType[] = [
      {
        label: item.hopeDate1?.slice(0, 16).replace("T", " ") as string,
        value: item.hopeDate1 as string,
      },
    ]

    if (item.hopeDate2?.slice(0, 10) !== "0001-01-01") {
      ret.push({
        label: item.hopeDate2?.slice(0, 16).replace("T", " ") as string,
        value: item.hopeDate2 as string,
      })
    }

    if (item.hopeDate3?.slice(0, 10) !== "0001-01-01") {
      ret.push({
        label: item.hopeDate3?.slice(0, 16).replace("T", " ") as string,
        value: item.hopeDate3 as string,
      })
    }

    return ret
  }

  const validate = async () => {
    let validation = true

    if (item.method === ADVISE_TYPE.ONLINE) {
      if (zoomlink) {
        setZoomValidationMessage("")
      } else {
        setZoomValidationMessage(t("please_input_zoom_link"))
        validation = validation && false
      }
    } else {
      if (areaID === undefined) {
        setAreaValidationMessage(t("please_select"))
        validation = validation && false
      } else {
        setAreaValidationMessage("")
      }
    }

    return validation
  }

  const handleAccept = async () => {
    if (!(await validate())) {
      return
    }

    let updateParams: any = {
      reservedDate: datetime,
      status: CONSULT_STATUS.ACCEPTED,
    }

    if (item.method === ADVISE_TYPE.ONLINE) {
      updateParams.meetingUrl = zoomlink
    } else {
      updateParams.areaID = areaID
    }

    setShowProgress(true)
    ConsultService.updateConsult(accessToken, item.id as number, updateParams)
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({
            show: true,
            text: t("success_accept_consult"),
            type: SNACKBAR_TYPES.SUCCESS,
          })
          handleOpen(false)
          handleItem(res.data)
        } else {
          setShowToast({ show: true, text: t("failed_accept_consult"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_accept_consult"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  return (
    <Modal
      open={open}
      onClose={() => handleOpen(false)}
      containerProps={{ px: "30px", py: "20px" }}
    >
      <Box sx={{ p: 0 }}>
        <Box display="flex" height="50px" sx={{ borderBottom: "2px solid #D4D3D7" }}>
          <Typography.InputFieldTitle
            sx={{
              fontFamily: "Noto Sans JP !important",
              fontWeight: "700",
              ml: "10px",
              my: "auto",
            }}
          >
            {t("title_consult_accept")}
          </Typography.InputFieldTitle>
          <CloseOutlinedIcon
            sx={{
              my: "auto",
              ml: "auto",
              mr: "20px",
              width: "30px",
              height: "30px",
              ":hover": { opacity: 0.8, cursor: "pointer" },
            }}
            onClick={() => handleOpen(false)}
          />
        </Box>
        <Box display="flex" sx={{ mt: "20px" }}>
          <Box display="flex">
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700" }}
            >
              {t("consult_member_name")}
            </Typography.DetailText>
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", ml: "20px" }}
            >
              {`${item.member?.lastName} ${item.member?.firstName}`}
            </Typography.DetailText>
          </Box>
        </Box>
        <Box sx={{ p: "10px", border: "1px solid #D4D3D7", mt: "20px" }}>
          <Typography.DetailText
            sx={{ fontFamily: "Noto Sans JP !important", fontWeight: "700", color: "#5A5A5A" }}
          >
            {t("consult_hope")}
          </Typography.DetailText>
          <Box sx={{ display: "flex", mt: "12px" }}>
            <Typography.DetailText
              sx={{ fontFamily: "Noto Sans JP !important", mr: "10px", color: "#5A5A5A" }}
            >
              {t("consult_hope_1")}
            </Typography.DetailText>
            <Typography.DetailText
              sx={{
                fontFamily: "Noto Sans JP !important",
                textDecoration: "underline",
                color: "#5A5A5A",
              }}
            >
              {item.hopeDate1?.slice(0, 16).replace("T", " ")}
            </Typography.DetailText>
          </Box>
          {item.hopeDate2?.slice(0, 10) !== "0001-01-01" && (
            <Box sx={{ display: "flex", mt: "12px" }}>
              <Typography.DetailText
                sx={{ fontFamily: "Noto Sans JP !important", mr: "10px", color: "#5A5A5A" }}
              >
                {t("consult_hope_2")}
              </Typography.DetailText>
              <Typography.DetailText
                sx={{
                  fontFamily: "Noto Sans JP !important",
                  textDecoration: "underline",
                  color: "#5A5A5A",
                }}
              >
                {item.hopeDate2?.slice(0, 16).replace("T", " ")}
              </Typography.DetailText>
            </Box>
          )}
          {item.hopeDate3?.slice(0, 10) !== "0001-01-01" && (
            <Box sx={{ display: "flex", mt: "12px" }}>
              <Typography.DetailText
                sx={{ fontFamily: "Noto Sans JP !important", mr: "10px", color: "#5A5A5A" }}
              >
                {t("consult_hope_3")}
              </Typography.DetailText>
              <Typography.DetailText
                sx={{
                  fontFamily: "Noto Sans JP !important",
                  textDecoration: "underline",
                  color: "#5A5A5A",
                }}
              >
                {item.hopeDate3?.slice(0, 16).replace("T", " ")}
              </Typography.DetailText>
            </Box>
          )}
        </Box>
        <Typography.InputFieldTitle
          sx={{
            fontFamily: "Noto Sans JP !important",
            fontWeight: "700",
            mt: "20px",
          }}
        >
          {t("accept_consult_stpe_1")}
        </Typography.InputFieldTitle>
        <SelectField
          datas={getReservationDate()}
          selected={datetime}
          handleSelected={setDatetime}
          sx={{ mt: "20px" }}
        />
        <Box sx={{ p: "10px", border: "1px solid #D4D3D7", mt: "20px" }}>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              fontWeight: "700",
            }}
          >
            {t("consult_method")}
          </Typography.DetailText>
          <Typography.DetailText
            sx={{
              fontFamily: "Noto Sans JP !important",
              color: "#998E8A",
            }}
          >
            {getMethodString()}
          </Typography.DetailText>
        </Box>

        {item.method === ADVISE_TYPE.ONLINE ? (
          <>
            <Typography.InputFieldTitle
              sx={{
                fontFamily: "Noto Sans JP !important",
                fontWeight: "700",
                mt: "20px",
              }}
            >
              {t("accept_consult_step_2_input_zoom")}
            </Typography.InputFieldTitle>
            <InputTextField
              text={zoomlink}
              handleText={setZoomLink}
              validationMessage={zoomValidationMessage}
              sx={{ mt: "20px", width: "100%" }}
              inputStyle={{ width: "100%" }}
              placeholder={t("please_input_zoom_link")}
            />
          </>
        ) : (
          <CheckBoxGroupField
            radio
            datas={areas}
            selected={areaID}
            handleSelected={setAreaID}
            name={t("accept_consult_step_2_area")}
            sx={{ mt: "20px" }}
            labelStyle={{
              fontFamily: "Noto Sans JP !important",
              fontSize: "16px",
              fontWeight: "700",
            }}
            validationMessage={areaValidationMessage}
          />
        )}
        <OutlinedButton
          caption={t("accept_consult")}
          sx={{ mt: "20px", borderColor: "primary.main" }}
          fontColor="primary.main"
          onClick={() => handleAccept()}
        />
      </Box>
    </Modal>
  )
}
